/**
 * Create-project modal — name input, calls the existing `POST /api/projects`
 * (via `api.createProject`). Shared by Projects, Dashboard and Home;
 * callers open the created project's workspace through `onCreated`.
 */
import { useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import { X, Loader2 } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useTranslations } from "@/hooks/use-translations";
import { useAuth } from "@/hooks/use-auth";
import { useActiveTeam } from "@/hooks/use-team";
import { api } from "@/lib/api";
import { toast } from "sonner";

interface CreateProjectModalProps {
	open: boolean;
	onClose: () => void;
	/** Called after the project is actually created, with its new chatId and name — caller decides where to navigate. */
	onCreated: (chatId: string, projectName: string) => void;
}

export function CreateProjectModal({ open, onClose, onCreated }: CreateProjectModalProps) {
	const t = useTranslations("projects");
	const { user } = useAuth();
	const { activeTeam } = useActiveTeam();
	const [newProjectName, setNewProjectName] = useState("");
	const [isCreating, setIsCreating] = useState(false);
	const [createError, setCreateError] = useState<string | null>(null);

	const handleClose = () => {
		if (isCreating) return;
		onClose();
		setCreateError(null);
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!newProjectName.trim() || !user?.id) return;

		setIsCreating(true);
		setCreateError(null);

		try {
			const result = await api.createProject({
				userId: user.id,
				...(activeTeam ? { teamId: activeTeam.id } : {}),
				projectName: newProjectName.trim(),
			});
			setNewProjectName("");
			onClose();
			onCreated(result.chatId, result.ProjectName);
		} catch (error) {
			const message =
				error instanceof Error ? error.message : "Failed to create project";
			setCreateError(message);
			toast.error(message);
		} finally {
			setIsCreating(false);
		}
	};

	return (
		<AnimatePresence>
			{open && (
				<>
					<motion.div
						className="fixed inset-0 bg-scrim/30 z-50"
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
						onClick={handleClose}
					/>
					<motion.div
						className="fixed inset-0 flex items-center justify-center z-50 p-4"
						initial={{ opacity: 0, scale: 0.95 }}
						animate={{ opacity: 1, scale: 1 }}
						exit={{ opacity: 0, scale: 0.95 }}
					>
						<Card
							className="w-full max-w-md shadow-popover"
							onClick={(e) => e.stopPropagation()}
						>
							<CardContent className="p-6">
								<div className="flex items-center justify-between mb-4">
									<h2 className="font-semibold text-text-primary">
										{t("addProject")}
									</h2>
									<Button
										type="button"
										variant="ghost"
										size="icon-sm"
										onClick={handleClose}
									>
										<X size={18} />
									</Button>
								</div>
								{createError && (
									<p className="text-sm text-danger mb-3">{createError}</p>
								)}
								<form onSubmit={handleSubmit}>
									<Input
										value={newProjectName}
										onChange={(e) => setNewProjectName(e.target.value)}
										placeholder={t("projectNamePlaceholder")}
										className="mb-4"
										autoFocus
										disabled={isCreating}
									/>
									<div className="flex gap-3 pt-2">
										<Button
											type="button"
											variant="secondary"
											onClick={handleClose}
											disabled={isCreating}
											className="flex-1"
										>
											{t("cancel")}
										</Button>
										<Button
											type="submit"
											disabled={isCreating || !newProjectName.trim()}
											className="flex-1"
										>
											{isCreating && (
												<Loader2 size={16} className="animate-spin" />
											)}
											{t("create")}
										</Button>
									</div>
								</form>
							</CardContent>
						</Card>
					</motion.div>
				</>
			)}
		</AnimatePresence>
	);
}
